import { Translate } from '@travel/translation';
import MealOption from '@travel/traveler-core/components/ItemFeatures/MealOption';
import PaymentMethod from '@travel/traveler-core/components/ItemFeatures/PaymentMethod';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import { stringify } from '@travel/utils';

import ImageGallery from 'components/ImageGallery';
import Link from 'core/universalRouter/Link';

import paths from 'constants/paths';

import styles from './planInfo.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { Plan } from 'PlanList-Types';

const PLAN_INFO_MEDIA_LIMIT = 3;

type Props = {
  query?: URLSearchQuery;
  /** String of hotel id */
  hotelId: string;
  /** Object containing a single plan data */
  plan: Plan;
  /** String fo accommodation area path for navigation */
  accommodationAreaPath: string;
  /** String fo accommodations area path for navigation */
  accommodationsPath: string;
};

const PlanInfo = (props: Props) => {
  const { hotelId, plan, query, accommodationAreaPath, accommodationsPath } = props;
  const { isPC } = useDeviceTypes();

  const planDetailsUrl = `${paths.planDetail.pathResolver(hotelId, plan.planId)}?${stringify({
    ...query,
    [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
    [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
  })}`;

  const planName = (
    <Link to={planDetailsUrl} className={styles.planName} data-testid="planInfo-planName-link">
      <h2>{plan.name}</h2>
    </Link>
  );

  const features = (
    <>
      <MealOption hasMeal={plan.hasMeal} spaceBetween={4} className={styles.iconEntry} />
      <PaymentMethod spaceBetween={4} className={styles.iconEntry} />
    </>
  );

  return (
    <div data-testid="planInfo-container">
      {isPC && planName}
      <div className={styles.planInfoContainer}>
        {isPC ? (
          <ImageGallery
            media={plan.media}
            galleryType="thumbnail"
            swiperClassName={styles.imageGalleryPC}
            galleryClassName={styles.imageWrapper}
            className={styles.mediaPC}
            thumbnailClass={styles.galleryThumbnail}
            data-testid="planInfo-swiper"
            mediaLimit={PLAN_INFO_MEDIA_LIMIT}
          />
        ) : (
          <ImageGallery
            media={plan.media}
            galleryType="slide"
            className={styles.media}
            swiperClassName={styles.imageGallery}
            galleryClassName={styles.imageWrapper}
            data-testid="planInfo-swiper"
            mediaLimit={PLAN_INFO_MEDIA_LIMIT}
          />
        )}
        <div className={styles.infoBox}>
          {isPC && plan.description && (
            <>
              {features}
              <p className={styles.planDescription} data-testid="planInfo-description">
                {plan.description}
              </p>
              <Link to={planDetailsUrl} className={styles.linkDetails} data-testid="planInfo-link">
                <Translate id="Plan_List.List.Display_Details" />
              </Link>
            </>
          )}
          {!isPC && (
            <>
              {planName}
              <div className={styles.iconList}>{features}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanInfo;
